
import { useMsal } from '@azure/msal-react';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { UserGroup, UserGroupName } from 'types/user.type';
import APIFetcher from '../components/remoteData/APIFetcher';

interface UserGroupContextValue {
  userGroups: UserGroup[] | null;
  userPermissions: string[]; // Lista med permission keys
  isLoading: boolean;
  isAdmin: boolean;
  isUserInGroup: (groupName: string) => boolean;
  hasPermission: (permissionKey: string) => boolean;
  refreshUserAccess: () => Promise<void>;
}

const UserGroupContext = createContext<UserGroupContextValue | undefined>(undefined);

interface UserGroupProviderProps {
  children: React.ReactNode;
}

export const UserGroupProvider: React.FC<UserGroupProviderProps> = ({ children }) => {
  const { instance } = useMsal();
  const [userGroups, setUserGroups] = useState<UserGroup[] | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const api = useMemo(() => new APIFetcher(instance), [instance]);

  const loadUserAccess = useCallback(async () => {
    try {
      setIsLoading(true);
      const groups = await api.getLoggedInUsersGroups();
      const permissions = await api.getLoggedInUsersPermissions();
      const permissionKeys = permissions.map(p => p.key);
      setUserGroups(groups);
      setUserPermissions(permissionKeys);
      localStorage.setItem('groups', JSON.stringify(groups));
      localStorage.setItem('permissions', JSON.stringify(permissions));
    } catch (error) {
      console.error('Failed to load user access:', error);
      setUserGroups([]);
      setUserPermissions([]);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void loadUserAccess();
  }, [loadUserAccess]);

  const isUserInGroup = useCallback(
    (groupName: string): boolean => {
      if (!userGroups || !groupName) return false;
      return userGroups.some(g => g.name === groupName && g.isMember);
    },
    [userGroups]
  );

  const hasPermission = useCallback(
    (permissionKey: string): boolean => {
      return userPermissions.includes(permissionKey);
    },
    [userPermissions]
  );

  const isAdmin = useMemo(() => isUserInGroup(UserGroupName.Admin), [isUserInGroup]);

  const contextValue: UserGroupContextValue = useMemo(
    () => ({
      userGroups,
      userPermissions,
      isLoading,
      isAdmin,
      isUserInGroup,
      hasPermission,
      refreshUserAccess: loadUserAccess,
    }),
    [userGroups, userPermissions, isLoading, isAdmin, isUserInGroup, hasPermission, loadUserAccess]
  );

  return <UserGroupContext.Provider value={contextValue}>{children}</UserGroupContext.Provider>;
};

export const useUserGroups = (): UserGroupContextValue => {
  const context = React.useContext(UserGroupContext);
  if (context === undefined) {
    throw new Error('useUserGroups must be used within a UserGroupProvider');
  }
  return context;
};
